import moment from 'moment';
import React, { Fragment } from 'react';
import If from 'src/components/Shared/If/If';
import Phone from 'src/components/Shared/Phone/Phone';
import { FundNoteBuy, TOLL_FREE_NUMBER } from 'src/settings/settings';
import AddressUndeliverableFundNoteTip from './AddressUndeliverableFundNoteTip';
import ESAPlanFundNoteTip from './ESAPlanFundNoteTip';
import FundNote from './FundNote';
import PendingTransactionFundNoteTip from './PendingTransactionFundNoteTip';
import RecentlyRedeemedFundNoteTip from './RecentlyRedeemedFundNoteTip';
import RestrictedCountryFundNoteTip from './RestrictedCountryFundNoteTip';
import SignatureRequiredFundNoteTip from './SignatureRequiredFundNoteTip';
import { FundNoteListProps } from './types';
import { scrollElementIntoFullView } from '../../../../utils/dom-utils';
import { setProgress } from '../../../../redux/modules/progress/actions';
import config from 'src/config/config';

const BuyFundNotes: React.FC<FundNoteListProps> = ({ fund }) => (
  <Fragment>
    <If test={fund.notes[FundNoteBuy.BUCKET_ROLL]}>
      <FundNote
        labelSubText={''}
        labelText="IRA Contributions Unavailable"
        tipHeader="IRA Contributions Unavailable"
        tipMessage={
          <div className="exclude">
            Sorry, contributions to your IRA(s) are temporarily unavailable due
            to year-end maintenance. Please check back after 7 PM EDT on January
            1st to make a contribution. If you are attempting to make a purchase
            into Government Money Fund or US Treasury Money Fund prior to 5pm on
            the last business day of the year, please{' '}
            <a href="https://www.troweprice.com/personal-investing/help/contact/index.html">
              contact us
            </a>
            .
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteBuy.APP_ZERO_DOLLAR_BALANCE]}>
      <FundNote
        labelSubText={''}
        labelText="Unable to Purchase"
        tipHeader="Unable to Purchase"
        tipMessage={
          <div className="exclude">
            The account minimum of $50,000 cannot be reached due to contribution
            limits.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteBuy.PENALTY_WITHHOLDING]}>
      <FundNote
        labelSubText=""
        labelText="IRS Backup or Penalty Tax Withholding"
        tipHeader="IRS Backup or Penalty Tax Withholding"
        tipMessage={
          <div className="exclude">
            There is an IRS backup or penalty tax withholding restriction on
            this fund. To learn more about these restrictions or make a
            transaction, please{' '}
            <a
              className="exclude"
              href={`${config.url}/personal-investing/help/contact/index.html`}
              rel="noopener noreferrer"
              target="_blank"
            >
              contact us
            </a>
            .
          </div>
        }
      />
    </If>

    <If test={fund.notes[FundNoteBuy.PENDING_STATUS]}>
      <FundNote labelText="Pending Bank Association" showToolTip={false} />
    </If>
    <If test={fund.notes[FundNoteBuy.MONEY_MARKET_EVENT_GATE_ACTIVE]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Redemption Block"
      />
    </If>
    <If test={fund.notes[FundNoteBuy.MONEY_MARKET_EVENT_GATE_REMOVED]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Redemption Block Removed"
      />
    </If>
    <If test={fund.notes[FundNoteBuy.MONEY_MARKET_EVENT_FEE_ACTIVE]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Liquidity Fee"
      />
    </If>
    <If test={fund.notes[FundNoteBuy.MONEY_MARKET_EVENT_FEE_REMOVED]}>
      <FundNote
        labelSubText={
          <div
            dangerouslySetInnerHTML={{ __html: fund.moneyMarketReformMessage }}
            className="exclude"
          />
        }
        labelText="Liquidity Fee Removed"
      />
    </If>
    <If test={fund.notes[FundNoteBuy.IRA_DECEASED]}>
      <FundNote
        labelSubText=""
        labelText="Change of Ownership Needed"
        tipHeader="Change of Ownership Needed"
        tipMessage={
          <div className="exclude">
            Change of Ownership Needed: Please{' '}
            <a
              className="exclude"
              href={`${config.url}/personal-investing/help/contact/index.html`}
              rel="noopener noreferrer"
              target="_blank"
            >
              contact us
            </a>{' '}
            to discuss options available with this account.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteBuy.INHERITED_FUND]}>
      <FundNote
        labelSubText=""
        labelText="Inherited IRA"
        tipHeader="Inherited IRA"
        tipMessage="You cannot purchase additional shares of an Inherited IRA."
      />
    </If>
    <If test={fund.notes[FundNoteBuy.CLOSED_NEW]}>
      <FundNote
        labelText="Closed to New Investors"
        tipHeader="Closed to New Investors"
        tipMessage="All shares previously redeemed, client cannot buy back into fund."
      />
    </If>
    <If test={fund.notes[FundNoteBuy.COUNTRY_RESTRICTED]}>
      <FundNote
        labelSubText="Requires Permanent US Address"
        labelText="Restricted"
        tipHeader="Restricted Country"
        tipMessage={<RestrictedCountryFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteBuy.ESA_PLAN]}>
      <FundNote
        labelSubText=""
        labelText="ESA Plan"
        tipHeader="ESA Plan"
        tipMessage={<ESAPlanFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteBuy.STOP_PURCHASE]}>
      <FundNote
        labelSubText=""
        labelText="Stop Purchase Applied"
        tipHeader="Stop Purchase"
        tipMessage={
          <div className="exclude">
            T. Rowe Price has blocked purchases on this fund or account.{' '}
            <a
              className="exclude"
              href="https://www.troweprice.com/personal-investing/help/contact/index.html"
              rel="noreferrer"
              target="_blank"
            >
              Contact us
            </a>{' '}
            for more information.
          </div>
        }
      />
    </If>

    <If test={fund.notes[FundNoteBuy.CANNOT_LINK_BANK]}>
      <FundNote
        labelSubText="Signature Required"
        labelText="Cannot Link Bank"
        tipHeader="Cannot Link Bank"
        tipMessage={<SignatureRequiredFundNoteTip />}
      />
    </If>

    <If test={fund.notes[FundNoteBuy.BANK_NOT_LINKED]}>
      <FundNote
        labelSubText=""
        labelText="Bank Not Linked"
        tipHeader="Bank Not Linked"
        tipMessage={
          <div className="exclude bankNotLinked">
            This portfolio is not connected to a bank on file. To continue,{' '}
            <a
              className="exclude college-branded-link"
              href={`${config.url}/usis/my-accounts/profile/linked-bank-accounts`}
            >
              link this bank
            </a>{' '}
            or{' '}
            <a
              className="exclude text-nowrap college-branded-link"
              onClick={() => {
                setProgress(1);
                scrollElementIntoFullView('funding-target-step');
              }}
            >
              choose a different bank.
            </a>
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteBuy.RECENTLY_REDEEMED]}>
      <FundNote
        labelSubText={`Available on or after ${moment(
          fund.purchaseBlockRemovalDate
        ).format('MM/DD')}.`}
        labelText="Recently Redeemed"
        tipHeader="Recently Redeemed"
        tipMessage={<RecentlyRedeemedFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteBuy.CANNOT_REOPEN_SUMMIT]}>
      <FundNote
        labelSubText=""
        labelText="Cannot Reopen"
        tipHeader="Cannot Reopen Fund"
        tipMessage={`Clients are not able to reopen ${fund?.name} by making contributions due to maximum IRA contribution limits.`}
      />
    </If>
    <If test={fund.notes[FundNoteBuy.CANNOT_REOPEN_ICLASS]}>
      <FundNote
        labelSubText=""
        labelText="Unable to Reopen Online"
        tipHeader="Unable to Reopen Online"
        tipMessage={
          <div className="exclude">
            Minimum required purchase is above online maximum. Call{' '}
            <Phone phoneNumber="(877) 218-5162" /> to reopen.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteBuy.ONLINE_PURCHASE_RESTRICTED]}>
      <FundNote
        labelSubText=""
        labelText="Unavailable for Purchase Online"
        tipHeader="Unavailable for Purchase Online"
        tipMessage={
          <div className="exclude">
            Online/phone purchase disabled. Call{' '}
            <Phone phoneNumber={TOLL_FREE_NUMBER} /> to enable purchasing.
          </div>
        }
      />
    </If>
    <If test={fund.notes[FundNoteBuy.PENDING]}>
      <FundNote
        labelSubText=""
        labelText="Transaction Pending"
        tipHeader="Transaction Pending"
        tipMessage={<PendingTransactionFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteBuy.ADDRESS_UNDELIVERABLE]}>
      <FundNote
        labelSubText=""
        labelText="Address Undeliverable"
        tipHeader="Address Undeliverable"
        tipMessage={<AddressUndeliverableFundNoteTip />}
      />
    </If>
    <If test={fund.notes[FundNoteBuy.EXISTING_ICLASS]}>
      <FundNote
        labelSubText=""
        labelText="I Class"
        tipHeader="I Class Share Pricing"
        tipMessage={
          <div>
            I Class shares have a lower expense ratio than Investor Class.
          </div>
        }
      />
    </If>
  </Fragment>
);

export default BuyFundNotes;
